/*
 * CustomerFavoritesContext — favori stoklar global state.
 *
 * Tek bir QR menü oturumunda tüm ProductCard'lar aynı kalp ikonu durumunu
 * paylaşır. Toggle anında optimistic update yapar, başarısız olursa geri
 * alır. Auth değişince (logout / login) listeyi otomatik yeniden yükler.
 *
 * Kullanım:
 *   <CustomerFavoritesProvider restaurantId={42}>
 *     <App />
 *   </CustomerFavoritesProvider>
 *
 *   const fav = useCustomerFavorites();
 *   fav.isFavorite(stockId);
 *   await fav.toggle(stockId);
 *   fav.list  // CustomerFavoriteStock[]
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CustomerFavoriteStock,
  addCustomerFavorite,
  getCustomerFavorites,
  removeCustomerFavorite,
  toggleCustomerFavorite,
} from '../api/customerFavorites';
import { useCustomerAuth } from './CustomerAuthContext';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface CustomerFavoritesContextValue {
  list: CustomerFavoriteStock[];
  /** stockId set — O(1) lookup ProductCard için. */
  ids: Set<number>;
  loading: boolean;
  error: string | null;
  /** Auth/route durumuna göre listeyi yenile. */
  refresh: () => Promise<void>;
  /** Toggle — optimistic UI; başarısız olursa eski state'e döner. */
  toggle: (stockId: number, hint?: Partial<CustomerFavoriteStock>) => Promise<boolean>;
  isFavorite: (stockId: number) => boolean;
  /** "Favorit yeməklər" kategorisini görünür mü? Ayarlardan kontrol edilir. */
  showFavoritesCategory: boolean;
  setShowFavoritesCategory: (v: boolean) => void;
}

const FAV_VISIBILITY_STORAGE_KEY = 'rezmo-show-favorites';

const defaultValue: CustomerFavoritesContextValue = {
  list: [],
  ids: new Set<number>(),
  loading: false,
  error: null,
  refresh: async () => {},
  toggle: async () => false,
  isFavorite: () => false,
  showFavoritesCategory: true,
  setShowFavoritesCategory: () => {},
};

const CustomerFavoritesContext =
  createContext<CustomerFavoritesContextValue>(defaultValue);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ProviderProps {
  children: ReactNode;
  /** Belirli bir restoranın favorilerini filtrele. null → tüm restoranlar. */
  restaurantId?: number | null;
}

export const CustomerFavoritesProvider: React.FC<ProviderProps> = ({
  children,
  restaurantId,
}) => {
  const { isAuthenticated, token } = useCustomerAuth();
  const [list, setList] = useState<CustomerFavoriteStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(0);

  // Ayarlardan kontrol edilen "Favorit kategorisi gözüksün mü?" flag'i.
  const [showFavoritesCategory, setShowFavoritesCategoryState] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(FAV_VISIBILITY_STORAGE_KEY);
      // Default: true. Sadece açıkça 'false' yazılmışsa gizle.
      return raw === null ? true : raw !== 'false';
    } catch {
      return true;
    }
  });

  const setShowFavoritesCategory = useCallback((v: boolean) => {
    setShowFavoritesCategoryState(v);
    try {
      localStorage.setItem(FAV_VISIBILITY_STORAGE_KEY, v ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, []);

  // O(1) lookup için Set
  const ids = useMemo(() => {
    const s = new Set<number>();
    for (const f of list) s.add(Number(f.stock_id));
    return s;
  }, [list]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setList([]);
      setError(null);
      return;
    }
    const ticket = ++inFlightRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomerFavorites(restaurantId ?? null);
      if (ticket === inFlightRef.current) {
        setList(data);
      }
    } catch (err: any) {
      if (ticket === inFlightRef.current) {
        // 401 ise CustomerAuthContext zaten temizliyor.
        if (err?.response?.status !== 401) {
          setError(err?.response?.data?.message || err?.message || 'Yüklənmədi');
        }
        setList([]);
      }
    } finally {
      if (ticket === inFlightRef.current) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, restaurantId]);

  // Auth değişince ya da restaurantId değişince listeyi yenile.
  useEffect(() => {
    refresh();
  }, [refresh, token]);

  const isFavorite = useCallback((stockId: number) => ids.has(Number(stockId)), [ids]);

  /** Toggle — optimistic. `hint` verilirse listeye eklenirken o veriler kullanılır
   *  (kart görünümünde "Bəyəndiyim" kategorisi anında dolsun). */
  const toggle = useCallback(
    async (stockId: number, hint?: Partial<CustomerFavoriteStock>): Promise<boolean> => {
      if (!isAuthenticated) {
        // Auth gerekli — caller tarafı bu durumu Dynamic Island ile yönetebilir.
        throw new Error('NOT_AUTHENTICATED');
      }
      const sid = Number(stockId);
      const wasFav = ids.has(sid);
      // Optimistic
      setList((cur) => {
        if (wasFav) {
          return cur.filter((f) => Number(f.stock_id) !== sid);
        }
        const optimistic: CustomerFavoriteStock = {
          stock_id: sid,
          name: hint?.name || '',
          image: hint?.image ?? null,
          price: typeof hint?.price === 'number' ? hint.price : 0,
          restaurant: hint?.restaurant ?? null,
          ...(hint || {}),
        };
        return [optimistic, ...cur];
      });

      try {
        const res = await toggleCustomerFavorite(sid);
        // Backend cevabı authoritative — mismatch varsa düzelt.
        setList((cur) => {
          const has = cur.some((f) => Number(f.stock_id) === sid);
          if (res.is_favorited && !has) {
            const optimistic: CustomerFavoriteStock = {
              stock_id: sid,
              name: hint?.name || '',
              image: hint?.image ?? null,
              price: typeof hint?.price === 'number' ? hint.price : 0,
              restaurant: hint?.restaurant ?? null,
              ...(hint || {}),
            };
            return [optimistic, ...cur];
          }
          if (!res.is_favorited && has) {
            return cur.filter((f) => Number(f.stock_id) !== sid);
          }
          return cur;
        });
        return res.is_favorited;
      } catch (err) {
        // Geri al
        setList((cur) => {
          const has = cur.some((f) => Number(f.stock_id) === sid);
          if (wasFav && !has) {
            // Eskiden favoriydi, optimistic ile çıkardık — listeye geri ekle
            const optimistic: CustomerFavoriteStock = {
              stock_id: sid,
              name: hint?.name || '',
              image: hint?.image ?? null,
              price: typeof hint?.price === 'number' ? hint.price : 0,
              restaurant: hint?.restaurant ?? null,
              ...(hint || {}),
            };
            return [optimistic, ...cur];
          }
          if (!wasFav && has) {
            return cur.filter((f) => Number(f.stock_id) !== sid);
          }
          return cur;
        });
        throw err;
      }
    },
    [ids, isAuthenticated],
  );

  // Tekil add/remove (toggle yerine semantik gerekirse). Şu anda toggle yeter.
  // Future-proof için yine de export ediyoruz; UI direkt çağırmaz.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unusedAdd = useCallback(async (stockId: number) => {
    await addCustomerFavorite(stockId);
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unusedRemove = useCallback(async (stockId: number) => {
    await removeCustomerFavorite(stockId);
  }, []);

  const value = useMemo<CustomerFavoritesContextValue>(
    () => ({
      list,
      ids,
      loading,
      error,
      refresh,
      toggle,
      isFavorite,
      showFavoritesCategory,
      setShowFavoritesCategory,
    }),
    [
      list,
      ids,
      loading,
      error,
      refresh,
      toggle,
      isFavorite,
      showFavoritesCategory,
      setShowFavoritesCategory,
    ],
  );

  return (
    <CustomerFavoritesContext.Provider value={value}>
      {children}
    </CustomerFavoritesContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useCustomerFavorites = (): CustomerFavoritesContextValue =>
  useContext(CustomerFavoritesContext);

export default CustomerFavoritesProvider;
